// swimlane-edges — pure edge/arc geometry for the swimlane overlay, shared by
// the export composer AND the live view (the scene-convergence peer of
// `bpmnEdgeGeom`). Every function is a pure function of the `swimlaneGeometry`
// pass + the shared `swimlaneTopology`, returning canvas-absolute geometry
// (`blockBounds`) that each backend wraps into its own shapes — the composer
// into `BpmnShape`s, the live view into React `<path>`s. No DOM measurement;
// no emission. Lifted coordinate-for-coordinate from the composer's
// golden-tested inline math, so the composed output is unchanged.

import type { Row, SwimlaneSceneInput } from "./types.js";
import type { SwimlaneGeometry, SwimlaneTopology } from "./swimlane-geometry.js";
import { showsBranchLabels } from "./swimlane-geometry.js";
import {
  blockBounds,
  obstaclesFromGeometry,
  optimizeBridges,
  polyPathPointAt,
  roundedPolylinePath,
  type RouteArrow,
  type Seg,
} from "./swimlane-routing.js";

// ─────────────────────────────────────────────────────────────────────────────
// Shared geometry primitives (canvas-absolute, scale-aware from geom.metrics).
// ─────────────────────────────────────────────────────────────────────────────

/** A gateway-arrow endpoint's bounds: a flow node's block, or (when the branch
 *  begins/ends with a nested gateway) that diamond's apex. `lane −1` blocks fall
 *  through to the row band, which is what the diamonds want. */
export interface SwimlaneGwBound {
  readonly i: number;
  readonly cx: number;
  readonly blockTop: number;
  readonly blockBottom: number;
  readonly blockCy: number;
}

/** Lane centre-x of a block (its horizontal midline). */
export function cxOfLane(li: number, geom: SwimlaneGeometry): number {
  const { LANE_WIDTH, BLOCK_INSET } = geom.metrics;
  return li * LANE_WIDTH + BLOCK_INSET + (LANE_WIDTH - BLOCK_INSET * 2) / 2;
}

/** Build the gateway-endpoint bounds resolver, shared by routing + gateway geom. */
export function makeGwBounds(
  geom: SwimlaneGeometry,
  topo: SwimlaneTopology,
): (r: Row) => SwimlaneGwBound {
  const { GW_HALF } = geom.metrics;
  const laneIndex = topo.laneIndexOf;
  const gwByFrame = new Map(topo.gateways.map((gg) => [gg.frame.id, gg] as const));
  return (r: Row): SwimlaneGwBound => {
    if (r.gateway && gwByFrame.has(r.gateway.frameId)) {
      const ng = gwByFrame.get(r.gateway.frameId)!;
      const nx = r.gateway.kind === "split" ? ng.splitX : ng.mergeX;
      const ncy = blockBounds(geom, r.y, -1).blockCy;
      return { i: laneIndex(r.toLaneId), cx: nx, blockTop: ncy - GW_HALF, blockBottom: ncy + GW_HALF, blockCy: ncy };
    }
    const li = laneIndex(r.toLaneId);
    return { i: li, cx: cxOfLane(li, geom), ...blockBounds(geom, r.y, li) };
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Flow routing — crossing-minimised bridge-Y per free flow/gateway trunk arrow.
// ─────────────────────────────────────────────────────────────────────────────

export interface SwimlaneRouting {
  /** flow-chain node idx → the bridge Y of its step→step arrow. */
  readonly flowMap: ReadonlyMap<number, number>;
  /** `<frameId>:fork|cont` → the bridge Y of that gateway trunk arrow. */
  readonly gatewayMap: ReadonlyMap<string, number>;
}

/** Global crossing-minimised routing for the flow overlay's free arrows (the
 *  gateway fork/cont trunks + step→step edges). Fixed obstacles = the branch
 *  fan buses + same-column verticals + up-and-over arcs. Lifted coordinate-for-
 *  coordinate from the composer; `optimizeBridges` is deterministic. */
export function swimlaneRouting(
  input: SwimlaneSceneInput,
  geom: SwimlaneGeometry,
  topo: SwimlaneTopology,
): SwimlaneRouting {
  const { LANE_WIDTH, BLOCK_INSET, GW_HALF, OVER_RISE } = geom.metrics;
  const laneIndex = topo.laneIndexOf;
  const { gateways, flowChain, gatewaySkip } = topo;
  const cx = (li: number): number => cxOfLane(li, geom);
  // Occupancy: every row with a resolvable callee lane (gateway + pick-hidden
  // rows included) — same set the composer builds.
  const occ = new Map<number, Set<number>>();
  for (const r of input.rows) {
    const li = laneIndex(r.toLaneId);
    if (li < 0) continue;
    let s = occ.get(r.y);
    if (!s) occ.set(r.y, (s = new Set()));
    s.add(li);
  }
  const hasNodeBetween = (y: number, a: number, b: number): boolean => {
    const s = occ.get(y);
    if (!s) return false;
    const lo = Math.min(a, b), hi = Math.max(a, b);
    for (let l = lo + 1; l < hi; l++) if (s.has(l)) return true;
    return false;
  };
  const gwBounds = makeGwBounds(geom, topo);
  const obstacles = obstaclesFromGeometry(geom);
  const free: RouteArrow[] = [];
  const fixed: Seg[] = [];
  const addGwArrow = (id: string, exitY: number, entryY: number, prevCx: number, targetCx: number, keys: string[]): void => {
    if (Math.abs(prevCx - targetCx) < 0.5) fixed.push({ x1: prevCx, y1: exitY, x2: targetCx, y2: entryY });
    else free.push({ id, exitY, entryY, prevCx, targetCx, selfKeys: new Set(keys) });
  };

  // Gateway trunk arrows (fork→split, merge→cont) route via the optimizer; the
  // split/tail fan buses are FIXED obstacles.
  for (const gw of gateways) {
    const splitCy = blockBounds(geom, gw.split.y, -1).blockCy;
    const mergeCy = blockBounds(geom, gw.merge.y, -1).blockCy;
    if (gw.fork && gw.fork.x === null) {
      const fb = gwBounds(gw.fork.row);
      addGwArrow(`gw:${gw.frame.id}:fork`, fb.blockBottom, splitCy - GW_HALF, fb.cx, gw.splitX, [`${gw.fork.row.y}:${fb.i}`]);
    }
    for (const b of gw.branches) {
      const hb = gwBounds(b.head);
      if (Math.abs(hb.cx - gw.splitX) >= 0.5) fixed.push({ x1: gw.splitX, y1: splitCy, x2: hb.cx, y2: splitCy });
      fixed.push({ x1: hb.cx, y1: splitCy, x2: hb.cx, y2: hb.blockTop });
    }
    for (const b of gw.branches) {
      if (b.tail.terminator || gw.detached) continue;
      const tb = gwBounds(b.tail);
      fixed.push({ x1: tb.cx, y1: tb.blockBottom, x2: tb.cx, y2: mergeCy });
      if (Math.abs(tb.cx - gw.mergeX) >= 0.5) fixed.push({ x1: tb.cx, y1: mergeCy, x2: gw.mergeX, y2: mergeCy });
    }
    if (gw.continuation && (gw.fork || !gw.detached)) {
      const c = gw.continuation;
      if (c.x === null) {
        const cb = gwBounds(c.row);
        addGwArrow(`gw:${gw.frame.id}:cont`, mergeCy + GW_HALF, cb.blockTop, gw.mergeX, cb.cx, [`${c.row.y}:${cb.i}`]);
      } else {
        const tCy = blockBounds(geom, c.row.y, -1).blockCy;
        addGwArrow(`gw:${gw.frame.id}:cont`, mergeCy + GW_HALF, tCy - GW_HALF, gw.mergeX, c.x, []);
      }
    }
  }

  // Main control-flow arrows.
  for (const { row, idx } of flowChain.nodes) {
    if (gatewaySkip.has(row)) continue;
    const prev = flowChain.prevOf.get(idx);
    if (!prev) continue;
    const toI = laneIndex(row.toLaneId);
    const prevToI = laneIndex(prev.toLaneId);
    if (toI < 0 || prevToI < 0) continue;
    const prevCx = cx(prevToI);
    const targetCx = cx(toI);
    if (prev.y === row.y) {
      const cyy = blockBounds(geom, row.y, toI).blockCy;
      const pBlockX = prevToI * LANE_WIDTH + BLOCK_INSET, pBlockW = LANE_WIDTH - BLOCK_INSET * 2;
      const blockX = toI * LANE_WIDTH + BLOCK_INSET, blockW = LANE_WIDTH - BLOCK_INSET * 2;
      const goingRight = toI >= prevToI;
      const prevTrailX = goingRight ? pBlockX + pBlockW : pBlockX;
      if (hasNodeBetween(row.y, prevToI, toI)) {
        const { blockTop } = blockBounds(geom, row.y, toI);
        const riseY = blockTop - OVER_RISE;
        fixed.push(
          { x1: prevTrailX, y1: cyy, x2: prevTrailX, y2: riseY },
          { x1: prevTrailX, y1: riseY, x2: targetCx, y2: riseY },
          { x1: targetCx, y1: riseY, x2: targetCx, y2: blockTop },
        );
      } else {
        const targetX = goingRight ? blockX : blockX + blockW;
        fixed.push({ x1: prevTrailX, y1: cyy, x2: targetX, y2: cyy });
      }
      continue;
    }
    const goingDown = row.y > prev.y;
    const pb = blockBounds(geom, prev.y, prevToI);
    const cb = blockBounds(geom, row.y, toI);
    const prevExitY = goingDown ? pb.blockBottom : pb.blockTop;
    const curEntryY = goingDown ? cb.blockTop : cb.blockBottom;
    if (Math.abs(prevCx - targetCx) < 0.5) {
      fixed.push({ x1: prevCx, y1: prevExitY, x2: targetCx, y2: curEntryY });
      continue;
    }
    free.push({ id: `flow:${idx}`, exitY: prevExitY, entryY: curEntryY, prevCx, targetCx, selfKeys: new Set([`${prev.y}:${prevToI}`, `${row.y}:${toI}`]) });
  }

  const chosen = optimizeBridges(free, obstacles, fixed);
  const flowMap = new Map<number, number>();
  const gatewayMap = new Map<string, number>();
  for (const [id, by] of chosen) {
    if (id.startsWith("flow:")) flowMap.set(Number(id.slice(5)), by);
    else if (id.startsWith("gw:")) gatewayMap.set(id.slice(3), by);
  }
  return { flowMap, gatewayMap };
}

// ─────────────────────────────────────────────────────────────────────────────
// Flow step→step arrows — the `d` per flow-chain node, keyed by node idx. The
// caller wraps each into its arrow shape + data-flow-step group and paints the
// entry-dot/dist marker (view-specific). Byte-identical to the composer.
// ─────────────────────────────────────────────────────────────────────────────

export function swimlaneFlowEdgeGeom(
  input: SwimlaneSceneInput,
  geom: SwimlaneGeometry,
  topo: SwimlaneTopology,
  flowMap: ReadonlyMap<number, number>,
): Map<number, string> {
  const { LANE_WIDTH, BLOCK_INSET, OVER_RISE } = geom.metrics;
  const laneIndex = topo.laneIndexOf;
  const { flowChain, gatewaySkip } = topo;
  const cx = (li: number): number => cxOfLane(li, geom);
  const occ = new Map<number, Set<number>>();
  for (const r of input.rows) {
    const li = laneIndex(r.toLaneId);
    if (li < 0) continue;
    let s = occ.get(r.y);
    if (!s) occ.set(r.y, (s = new Set()));
    s.add(li);
  }
  const hasNodeBetween = (y: number, a: number, b: number): boolean => {
    const s = occ.get(y);
    if (!s) return false;
    const lo = Math.min(a, b), hi = Math.max(a, b);
    for (let l = lo + 1; l < hi; l++) if (s.has(l)) return true;
    return false;
  };
  const out = new Map<number, string>();
  for (const { row, idx } of flowChain.nodes) {
    if (gatewaySkip.has(row)) continue;
    const prev = flowChain.prevOf.get(idx) ?? null;
    if (!prev) continue;
    const toI = laneIndex(row.toLaneId);
    if (toI < 0) continue;
    const prevToI = laneIndex(prev.toLaneId);
    if (prevToI < 0) continue;
    const blockX = toI * LANE_WIDTH + BLOCK_INSET;
    const blockW = LANE_WIDTH - BLOCK_INSET * 2;
    const cyy = blockBounds(geom, row.y, toI).blockCy;
    const goingRight = toI >= prevToI;
    let d: string;
    if (prev.y === row.y) {
      const prevTrailX = goingRight ? prevToI * LANE_WIDTH + LANE_WIDTH - BLOCK_INSET : prevToI * LANE_WIDTH + BLOCK_INSET;
      d = hasNodeBetween(row.y, prevToI, toI)
        ? overArcPath(prevTrailX, cyy, blockX + blockW / 2, blockBounds(geom, row.y, toI).blockTop, OVER_RISE)
        : `M ${r2(prevTrailX)} ${r2(cyy)} L ${r2(goingRight ? blockX : blockX + blockW)} ${r2(cyy)}`;
    } else {
      const prevCenterX = cx(prevToI);
      const targetCx = blockX + blockW / 2;
      const goingDown = row.y > prev.y;
      const pb = blockBounds(geom, prev.y, prevToI);
      const cb = blockBounds(geom, row.y, toI);
      const prevExitY = goingDown ? pb.blockBottom : pb.blockTop;
      const currentEntryY = goingDown ? cb.blockTop : cb.blockBottom;
      d = Math.abs(prevCenterX - targetCx) < 0.5
        ? `M ${r2(prevCenterX)} ${r2(prevExitY)} L ${r2(targetCx)} ${r2(currentEntryY)}`
        : zBridgePath(prevCenterX, prevExitY, targetCx, currentEntryY, flowMap.get(idx) ?? (prevExitY + currentEntryY) / 2);
    }
    out.set(idx, d);
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Flow-variant aux edges (saga drops / reverse compensation chain / `go`
// back-arcs). Endpoints are resolved row-index pairs; global left-to-right ox
// ranking + per-target run-in level keep converging arcs from stacking. The
// caller wraps each `d` into its shape and paints the midpoint comment.
// ─────────────────────────────────────────────────────────────────────────────

export interface SwimlaneAuxPath {
  readonly id: string;
  readonly kind: SwimlaneAuxKind;
  /** Canvas-absolute routed path. */
  readonly d: string;
  /** `go "note"` — the arc's midpoint label (only on a `reentry`). */
  readonly comment?: string;
  /** Midpoint of `d` (only set when `comment`), for the label anchor. */
  readonly midX?: number;
  readonly midY?: number;
}

export function swimlaneFlowAux(
  input: SwimlaneSceneInput,
  geom: SwimlaneGeometry,
  topo: SwimlaneTopology,
  auxEdges: readonly SwimlaneAuxEdge[],
): SwimlaneAuxPath[] {
  const { LANE_WIDTH, BLOCK_INSET, OVER_RISE, AUX_HEAD } = geom.metrics;
  const laneIndex = topo.laneIndexOf;
  const hidden = topo.hiddenRowIndexes;
  const cx = (li: number): number => cxOfLane(li, geom);
  const occ = new Map<number, Set<number>>();
  for (const r of input.rows) {
    const li = laneIndex(r.toLaneId);
    if (li < 0) continue;
    let s = occ.get(r.y);
    if (!s) occ.set(r.y, (s = new Set()));
    s.add(li);
  }
  const laneOccupied = (y: number, lane: number): boolean => occ.get(y)?.has(lane) ?? false;
  const hasNodeBetween = (y: number, a: number, b: number): boolean => {
    const s = occ.get(y);
    if (!s) return false;
    const lo = Math.min(a, b), hi = Math.max(a, b);
    for (let l = lo + 1; l < hi; l++) if (s.has(l)) return true;
    return false;
  };
  const laneBlockedBetween = (y0: number, y1: number, lane: number): boolean => {
    const lo = Math.min(y0, y1), hi = Math.max(y0, y1);
    for (let y = lo + 1; y < hi; y++) if (laneOccupied(y, lane)) return true;
    return false;
  };

  const aux = auxEdges.filter((e) => input.rows[e.fromRowIndex] && input.rows[e.toRowIndex]);
  if (!aux.length) return [];
  // PASS 1 — resolve geometry + classify.
  const items = aux
    .map((e) => {
      const from = input.rows[e.fromRowIndex]!;
      const to = input.rows[e.toRowIndex]!;
      if (hidden.has(e.fromRowIndex) || hidden.has(e.toRowIndex)) return null;
      const fromI = laneIndex(from.toLaneId), toI = laneIndex(to.toLaneId);
      if (fromI < 0 || toI < 0) return null;
      const prevCx = cx(fromI), targetCx = cx(toI);
      const goingDown = to.y > from.y;
      const fb = blockBounds(geom, from.y, fromI);
      const tb = blockBounds(geom, to.y, toI);
      const exitY = goingDown ? fb.blockBottom : fb.blockTop;
      const entryY = goingDown ? tb.blockTop : tb.blockBottom;
      const sameRow = from.y === to.y && Math.abs(prevCx - targetCx) >= 0.5;
      const sameLane = Math.abs(prevCx - targetCx) < 0.5;
      return { e, from, to, fromI, toI, prevCx, targetCx, goingDown, fb, tb, exitY, entryY, sameRow, sameLane };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);
  const crossRow = items.filter((it) => !it.sameRow);
  const ranked = crossRow
    .map((it, idx) => ({ it, idx, left: Math.min(it.prevCx, it.targetCx) }))
    .sort((p, q) => p.left - q.left || p.idx - q.idx);
  const oxOf = new Map<string, number>();
  ranked.forEach((r, rank) => oxOf.set(r.it.e.id, Math.max(-22, Math.min(22, (rank - (ranked.length - 1) / 2) * 10))));
  const byTargetCount = new Map<string, number>();
  const byRankOf = new Map<string, number>();
  for (const it of crossRow) {
    const key = `${it.to.y}:${it.toI}`;
    const r = byTargetCount.get(key) ?? 0;
    byRankOf.set(it.e.id, r);
    byTargetCount.set(key, r + 1);
  }
  // PASS 2 — route.
  const out: SwimlaneAuxPath[] = [];
  let auxGutter = 0;
  for (const it of items) {
    const { e, from, to, fromI, prevCx, targetCx, goingDown, fb, tb, exitY, entryY, sameRow, sameLane } = it;
    const ox = oxOf.get(e.id) ?? 0;
    const byOff = (byRankOf.get(e.id) ?? 0) * 6;
    let d: string;
    if (sameRow) {
      const cyy = fb.blockCy;
      const goingRight = it.toI > fromI;
      const srcEdge = goingRight ? fromI * LANE_WIDTH + LANE_WIDTH - BLOCK_INSET : fromI * LANE_WIDTH + BLOCK_INSET;
      const tgtEdge = goingRight ? it.toI * LANE_WIDTH + BLOCK_INSET : it.toI * LANE_WIDTH + LANE_WIDTH - BLOCK_INSET;
      d = hasNodeBetween(from.y, fromI, it.toI)
        ? `M ${r2(srcEdge)} ${r2(cyy)} L ${r2(srcEdge)} ${r2(fb.blockTop - OVER_RISE)} L ${r2(targetCx)} ${r2(fb.blockTop - OVER_RISE)} L ${r2(targetCx)} ${r2(tb.blockTop)}`
        : `M ${r2(srcEdge)} ${r2(cyy)} L ${r2(tgtEdge)} ${r2(cyy)}`;
    } else if (sameLane) {
      const px = prevCx + ox;
      d = laneBlockedBetween(from.y, to.y, fromI)
        ? `M ${r2(px)} ${r2(exitY)} L ${r2(fromI * LANE_WIDTH + LANE_WIDTH - BLOCK_INSET + 8)} ${r2(exitY)} L ${r2(fromI * LANE_WIDTH + LANE_WIDTH - BLOCK_INSET + 8)} ${r2(entryY)} L ${r2(px)} ${r2(entryY)}`
        : `M ${r2(px)} ${r2(exitY)} L ${r2(px)} ${r2(entryY)}`;
    } else if (Math.abs(to.y - from.y) > 1 && laneBlockedBetween(from.y, to.y, fromI)) {
      const dir = targetCx >= prevCx ? 1 : -1;
      const gx = prevCx + dir * (56 + (auxGutter++ % 3) * 9);
      const y1 = goingDown ? exitY + 8 : exitY - 8;
      const y2 = goingDown ? entryY - AUX_HEAD - byOff : entryY + AUX_HEAD + byOff;
      const tx = targetCx + ox;
      d = `M ${r2(prevCx)} ${r2(exitY)} L ${r2(prevCx)} ${r2(y1)} L ${r2(gx)} ${r2(y1)} L ${r2(gx)} ${r2(y2)} L ${r2(tx)} ${r2(y2)} L ${r2(tx)} ${r2(entryY)}`;
    } else {
      const lo = Math.min(exitY, entryY) + 3, hi = Math.max(exitY, entryY) - 3;
      const by = entryY > exitY ? Math.max(lo, entryY - AUX_HEAD - byOff) : Math.min(hi, entryY + AUX_HEAD + byOff);
      const px = prevCx + ox, tx = targetCx + ox;
      d = `M ${r2(px)} ${r2(exitY)} L ${r2(px)} ${r2(by)} L ${r2(tx)} ${r2(by)} L ${r2(tx)} ${r2(entryY)}`;
    }
    const mid = e.comment ? polyPathPointAt(d, 0.5) : null;
    out.push({ id: `aux:${e.id}`, kind: e.kind, d, comment: e.comment, midX: mid?.[0], midY: mid?.[1] });
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Gateway leg geometry (flow) — the fork / head / tail / continuation leg paths
// + diamond positions + merge flags. Geometry-only: the caller paints the
// diamond triangles, merge glyph, threshold/collapse labels, and maps each
// leg's role/isCatch to a stroke + arrowhead. Lifted from the composer.
// ─────────────────────────────────────────────────────────────────────────────

/** Classify a `parallel` merge for its diamond glyph (PROCESS.md §5). Shared. */
export function mergeGlyphKind(mergeAspect: string | undefined): "join" | "race" | "await" | null {
  if (!mergeAspect) return "join"; // bare `parallel { … }` — the AND-join
  if (mergeAspect.startsWith("out")) return null; // detached — no merge diamond
  if (mergeAspect.startsWith("select")) return null; // inclusive-OR, not an AND-join
  if (mergeAspect.startsWith("race")) return "race";
  if (mergeAspect.startsWith("await")) return "await";
  return "join"; // `join N` and boolean-name thresholds are AND/threshold joins
}

export type SwimlaneGwRole = "fork" | "head" | "tail" | "cont";

/** One gateway leg: its path + role + arm flags. The caller resolves the stroke
 *  and arrowhead from `role`/`isCatch`; only head legs carry a display `label`.
 *  `branchLabel` (head/tail legs) is the branch's identity for edge-id / pick
 *  lookup — always present even when the display label is suppressed. */
export interface SwimlaneGwLeg {
  readonly d: string;
  readonly role: SwimlaneGwRole;
  readonly isCatch: boolean;
  readonly branchLabel?: string;
  readonly label?: string;
  readonly labelX?: number;
  readonly labelY?: number;
}

export interface SwimlaneGwGeom {
  readonly frameId: string;
  readonly splitX: number;
  readonly splitCy: number;
  readonly mergeX: number;
  readonly mergeCy: number;
  readonly hiddenCount: number;
  readonly detached: boolean;
  readonly mergeKind: "join" | "race" | "await" | null;
  readonly mergeAspect?: string;
  readonly legs: SwimlaneGwLeg[];
}

export function swimlaneGatewayGeom(
  input: SwimlaneSceneInput,
  geom: SwimlaneGeometry,
  topo: SwimlaneTopology,
  gatewayMap: ReadonlyMap<string, number>,
): SwimlaneGwGeom[] {
  const { LANE_WIDTH, BLOCK_INSET, GW_HALF, GUTTER_BASE, GUTTER_TRACK } = geom.metrics;
  const laneIndex = topo.laneIndexOf;
  const gateways = topo.gateways;
  const gwBounds = makeGwBounds(geom, topo);
  const gwByFrame = new Map(gateways.map((gg) => [gg.frame.id, gg] as const));
  const isGwNode = (r: Row): boolean => !!(r.gateway && gwByFrame.has(r.gateway.frameId));
  const out: SwimlaneGwGeom[] = [];
  for (const gw of gateways) {
    const fid = gw.frame.id;
    const splitX = gw.splitX, mergeX = gw.mergeX;
    const splitCy = blockBounds(geom, gw.split.y, -1).blockCy;
    const mergeCy = blockBounds(geom, gw.merge.y, -1).blockCy;
    const route = (key: string, sx: number, sy: number, tx: number, ty: number): string => {
      if (Math.abs(sx - tx) < 0.5) return `M ${r2(sx)} ${r2(sy)} L ${r2(tx)} ${r2(ty)}`;
      return zBridgePath(sx, sy, tx, ty, gatewayMap.get(key) ?? (sy + ty) / 2);
    };
    const labeled = showsBranchLabels(gw.frame.kind);
    const blkL = (li: number): number => li * LANE_WIDTH + BLOCK_INSET;
    const blkR = (li: number): number => li * LANE_WIDTH + LANE_WIDTH - BLOCK_INSET;
    const lanesWithGutter = (side: "head" | "tail"): Map<string, Set<number>> => {
      const m = new Map<string, Set<number>>();
      for (const g2 of gateways) {
        const count = new Map<number, number>();
        for (const b of g2.branches) {
          if (side === "tail" && b.tail.terminator) continue;
          const li = laneIndex((side === "head" ? b.head : b.tail).toLaneId);
          if (li >= 0) count.set(li, (count.get(li) ?? 0) + 1);
        }
        const s = new Set<number>();
        count.forEach((n2, li) => { if (n2 >= 2) s.add(li); });
        m.set(g2.frame.id, s);
      }
      return m;
    };
    const headGutterLanes = lanesWithGutter("head");
    const tailGutterLanes = lanesWithGutter("tail");
    const levelIn = (li: number): number => gw.split.enclosingFrames.filter((f) => headGutterLanes.get(f)?.has(li)).length - 1;
    const levelOut = (li: number): number => gw.merge.enclosingFrames.filter((f) => tailGutterLanes.get(f)?.has(li)).length - 1;
    const gutInAt = (li: number, lvl: number): number => li * LANE_WIDTH + Math.min(GUTTER_BASE + Math.max(lvl, 0) * GUTTER_TRACK, BLOCK_INSET - 3);
    const gutOutAt = (li: number, lvl: number): number => (li + 1) * LANE_WIDTH - Math.min(GUTTER_BASE + Math.max(lvl, 0) * GUTTER_TRACK, BLOCK_INSET - 3);
    const rankWithin = (rows: readonly Row[], asc: boolean) => {
      const groups = new Map<number, { r: Row; b: (typeof gw.branches)[number] }[]>();
      gw.branches.forEach((b, i) => {
        const li = laneIndex(rows[i]!.toLaneId);
        (groups.get(li) ?? groups.set(li, []).get(li)!).push({ r: rows[i]!, b });
      });
      const rank = new Map<(typeof gw.branches)[number], { k: number; li: number; count: number }>();
      groups.forEach((arr, li) => {
        arr.sort((a, c) => (asc ? a.r.y - c.r.y : c.r.y - a.r.y));
        arr.forEach((e, k) => rank.set(e.b, { k, li, count: arr.length }));
      });
      return rank;
    };
    const headRank = rankWithin(gw.branches.map((b) => b.head), true);
    const tailRank = rankWithin(gw.branches.map((b) => b.tail), false);
    const corridorBlocked = (li: number, yTop: number, yBottom: number, self: Row): boolean => {
      for (const r of input.rows) {
        if (r === self || r.gateway || r.subprocessHeader) continue;
        if (r.stepIndex === null && !r.terminator) continue;
        if (laneIndex(r.toLaneId) !== li) continue;
        const rb = gwBounds(r);
        if (rb.blockTop < yBottom - 1 && rb.blockBottom > yTop + 1) return true;
      }
      return false;
    };

    const legs: SwimlaneGwLeg[] = [];
    if (gw.fork && gw.fork.x === null) {
      const fb = gwBounds(gw.fork.row);
      legs.push({ d: route(`${fid}:fork`, fb.cx, fb.blockBottom, splitX, splitCy - GW_HALF), role: "fork", isCatch: false });
    }
    for (const b of gw.branches) {
      const hb = gwBounds(b.head);
      const { li, count } = headRank.get(b)!;
      let d: string;
      let labelX: number;
      if (count === 1 && !corridorBlocked(li, splitCy, hb.blockTop, b.head)) {
        d = Math.abs(hb.cx - splitX) < 0.5
          ? `M ${r2(splitX)} ${r2(splitCy)} L ${r2(hb.cx)} ${r2(hb.blockTop)}`
          : `M ${r2(splitX)} ${r2(splitCy)} L ${r2(hb.cx)} ${r2(splitCy)} L ${r2(hb.cx)} ${r2(hb.blockTop)}`;
        labelX = blkL(li);
      } else {
        const gut = gutInAt(li, levelIn(li));
        d = isGwNode(b.head)
          ? `M ${r2(splitX)} ${r2(splitCy)} L ${r2(gut)} ${r2(splitCy)} L ${r2(gut)} ${r2(hb.blockTop - 8)} L ${r2(hb.cx)} ${r2(hb.blockTop - 8)} L ${r2(hb.cx)} ${r2(hb.blockTop)}`
          : `M ${r2(splitX)} ${r2(splitCy)} L ${r2(gut)} ${r2(splitCy)} L ${r2(gut)} ${r2(hb.blockCy)} L ${r2(blkL(li))} ${r2(hb.blockCy)}`;
        labelX = gut;
      }
      legs.push({
        d, role: "head", isCatch: b.isCatch, branchLabel: b.label,
        ...(labeled && b.label ? { label: b.label, labelX: labelX + 4, labelY: hb.blockTop - 6 } : {}),
      });
    }
    for (const b of gw.branches) {
      if (b.tail.terminator || gw.detached) continue;
      const tb = gwBounds(b.tail);
      const { li, count } = tailRank.get(b)!;
      const sideX = tb.cx < mergeX ? mergeX - GW_HALF : tb.cx > mergeX ? mergeX + GW_HALF : mergeX;
      let d: string;
      if (count === 1 && !corridorBlocked(li, tb.blockBottom, mergeCy, b.tail)) {
        d = Math.abs(tb.cx - mergeX) < 0.5
          ? `M ${r2(tb.cx)} ${r2(tb.blockBottom)} L ${r2(mergeX)} ${r2(mergeCy - GW_HALF)}`
          : `M ${r2(tb.cx)} ${r2(tb.blockBottom)} L ${r2(tb.cx)} ${r2(mergeCy)} L ${r2(sideX)} ${r2(mergeCy)}`;
      } else {
        const gut = gutOutAt(li, levelOut(li));
        d = isGwNode(b.tail)
          ? `M ${r2(tb.cx)} ${r2(tb.blockBottom)} L ${r2(tb.cx)} ${r2(tb.blockBottom + 8)} L ${r2(gut)} ${r2(tb.blockBottom + 8)} L ${r2(gut)} ${r2(mergeCy)} L ${r2(sideX)} ${r2(mergeCy)}`
          : `M ${r2(blkR(li))} ${r2(tb.blockCy)} L ${r2(gut)} ${r2(tb.blockCy)} L ${r2(gut)} ${r2(mergeCy)} L ${r2(sideX)} ${r2(mergeCy)}`;
      }
      legs.push({ d, role: "tail", isCatch: b.isCatch, branchLabel: b.label });
    }
    if (gw.continuation && (gw.fork || !gw.detached)) {
      const c = gw.continuation;
      let startX = mergeX, startY = mergeCy + GW_HALF;
      if (gw.detached && gw.fork) {
        const fb = gwBounds(gw.fork.row);
        startX = gw.fork.x === null ? fb.cx : gw.fork.x;
        startY = gw.fork.x === null ? fb.blockBottom : fb.blockCy + GW_HALF;
      }
      if (c.x === null) {
        const cb = gwBounds(c.row);
        legs.push({ d: route(`${fid}:cont`, startX, startY, cb.cx, cb.blockTop), role: "cont", isCatch: false });
      } else {
        const tCy = blockBounds(geom, c.row.y, -1).blockCy;
        legs.push({ d: route(`${fid}:cont`, startX, startY, c.x, tCy - GW_HALF), role: "cont", isCatch: false });
      }
    }

    const mergeKind = gw.frame.kind === "parallel" ? mergeGlyphKind(gw.frame.mergeAspect) : null;
    const mergeAspect =
      gw.frame.mergeAspect ??
      (gw.frame.runCount !== undefined ? `≤${gw.frame.runCount} of ${gw.frame.branches.length}` : undefined);
    out.push({ frameId: fid, splitX, splitCy, mergeX, mergeCy, hiddenCount: gw.hiddenCount, detached: gw.detached, mergeKind, mergeAspect, legs });
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Aux edges — a non-forward back-edge (saga drop / reverse compensation chain /
// `go` re-entry). Endpoints are resolved to LAYOUT-ROW indexes by the caller
// (headless adapters map them from the walk's `compensation`/`reentries`; the
// live view resolves them from its `flow-graph` the same way) so this module
// stays free of the flow-graph node-key indirection.
// ─────────────────────────────────────────────────────────────────────────────

export type SwimlaneAuxKind = "drop" | "comp" | "reentry";

export interface SwimlaneAuxEdge {
  readonly id: string;
  readonly kind: SwimlaneAuxKind;
  readonly fromRowIndex: number;
  readonly toRowIndex: number;
  /** `go "note"` — rendered as the re-entry arc's midpoint label. */
  readonly comment?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sequence variant: the right-side back-arcs (re-entry + saga comp/drop).
// ─────────────────────────────────────────────────────────────────────────────

/** One sequence back-arc's resolved geometry. The caller supplies stroke/marker
 *  tokens per `kind` and the comment glyph at `commentX`/`commentY`. */
export interface SwimlaneSeqArc {
  /** Echoes the input aux edge `id` so a keyed caller can map arcs back
   *  (the pass skips hidden/unresolvable edges, so index alignment is lost). */
  readonly id: string;
  readonly kind: SwimlaneAuxKind;
  /** Right-side rounded back-arc `d` (canvas-absolute). */
  readonly d: string;
  /** The source row's branch is unselected → the whole arc dims. */
  readonly dimmed: boolean;
  /** Layout-row index of the arc's source (→ `data-seq-reentry`). */
  readonly fromRowIndex: number;
  /** `go "note"` — only ever set on a `reentry` arc. */
  readonly comment?: string;
  /** Midpoint anchor for the comment label (only meaningful when `comment`). */
  readonly commentX: number;
  readonly commentY: number;
}

/** The sequence-variant back-arcs for every resolvable aux edge, in input order.
 *  Hidden-row endpoints and unresolvable lanes are skipped (as the composer
 *  does). Geometry is byte-identical to the composer's committed seq-arc loop. */
export function swimlaneSeqArcs(
  input: SwimlaneSceneInput,
  geom: SwimlaneGeometry,
  topo: SwimlaneTopology,
  auxEdges: readonly SwimlaneAuxEdge[],
): SwimlaneSeqArc[] {
  const laneIndex = topo.laneIndexOf;
  const hidden = topo.hiddenRowIndexes;
  const { LANE_WIDTH, BLOCK_INSET } = geom.metrics; // scale-aware — match the composer
  const out: SwimlaneSeqArc[] = [];
  for (const e of auxEdges) {
    const from = input.rows[e.fromRowIndex];
    const to = input.rows[e.toRowIndex];
    if (!from || !to) continue;
    if (hidden.has(e.fromRowIndex) || hidden.has(e.toRowIndex)) continue;
    const fLane = laneIndex(from.toLaneId);
    const tLane = laneIndex(to.toLaneId);
    if (fLane < 0 || tLane < 0) continue;
    const fy = blockBounds(geom, from.y, fLane).blockCy;
    const ty = blockBounds(geom, to.y, tLane).blockCy;
    const fX = fLane * LANE_WIDTH + LANE_WIDTH - BLOCK_INSET;
    const tX = tLane * LANE_WIDTH + LANE_WIDTH - BLOCK_INSET;
    const outX = Math.max(fX, tX) + 18;
    out.push({
      id: e.id,
      kind: e.kind,
      d: seqReentryArcPath(fX, fy, tX, ty),
      dimmed: from.branchSelected === false,
      fromRowIndex: e.fromRowIndex,
      comment: e.comment,
      commentX: outX + 4,
      commentY: (fy + ty) / 2 - 4,
    });
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pure d-builders (shared by both backends). Committed formulas — do not tweak.
// (The flow-routing builders `zBridgePath`/`overArcPath`/`mergeGlyphKind` are
// lifted alongside the routing/gateway slices — kept in the composer until then.)
// ─────────────────────────────────────────────────────────────────────────────

const r2 = (n: number): number => Math.round(n * 100) / 100;

/** Committed sequence self-call arc (right-side loop off the block). */
export function seqSelfCallArc(blockRight: number, cy: number): string {
  return `M ${r2(blockRight)} ${r2(cy - 6)} q 14 0 14 12 q 0 12 -14 12`;
}

/** Committed sequence `go` re-entry arc (right-side rounded back-arc). */
export function seqReentryArcPath(fX: number, fy: number, tX: number, ty: number): string {
  const outX = Math.max(fX, tX) + 18;
  return roundedPolylinePath([[fX, fy], [outX, fy], [outX, ty], [tX, ty]], 6);
}

/** Committed Z-bridge: vertical → horizontal at `by` → vertical. */
export function zBridgePath(sx: number, sy: number, tx: number, ty: number, by: number): string {
  return `M ${r2(sx)} ${r2(sy)} L ${r2(sx)} ${r2(by)} L ${r2(tx)} ${r2(by)} L ${r2(tx)} ${r2(ty)}`;
}

/** Committed same-row up-and-over arc (a node sits between source and target).
 *  `overRise` is scale-aware (geom.metrics.OVER_RISE). */
export function overArcPath(srcX: number, cy: number, targetCx: number, blockTop: number, overRise: number): string {
  const riseY = blockTop - overRise;
  return `M ${r2(srcX)} ${r2(cy)} L ${r2(srcX)} ${r2(riseY)} L ${r2(targetCx)} ${r2(riseY)} L ${r2(targetCx)} ${r2(blockTop)}`;
}
